import { useCallback, useMemo } from 'react';

import { Checkbox, CheckboxGroup } from '@twilio-paste/core/checkbox';
import { FormControl } from '@twilio-paste/core/form';
import { HelpText } from '@twilio-paste/core/help-text';
import { useFormikContext } from 'formik';

import { AppChannel } from '../../../modules/Apps';
import { useCheckBoxField } from '../../common/CheckBox';

import { AppSettingsFormValues } from './AppSettingsForm';

const appChannelLabel: Record<AppChannel, string> = {
  EMAIL: 'Email',
  PUSH: 'Push',
  SMS: 'Sms',
  VOICE: 'Voice',
};

interface AppChannelsSectionProps {
  readOnly?: boolean;
}

export function AppChannelsSection({ readOnly }: AppChannelsSectionProps) {
  const {
    touched,
    errors,
    values: { channels = [] },
  } = useFormikContext<AppSettingsFormValues>();

  const handleChange = useCheckBoxField<AppSettingsFormValues, AppChannel>('channels');

  const isChecked = useCallback((channel: AppChannel) => channels.includes(channel), [channels]);

  const appChannels = useMemo(
    () =>
      Object.values(AppChannel).map(channel => {
        return (
          <Checkbox
            key={channel}
            id={channel}
            value={channel}
            checked={isChecked(channel)}
            onChange={handleChange}
            readOnly={readOnly}
          >
            {appChannelLabel[channel]}
          </Checkbox>
        );
      }),
    [handleChange, isChecked, readOnly],
  );

  return (
    <FormControl>
      <CheckboxGroup
        name="channels"
        legend="Channels"
        helpText="Select the channels enabled for us with this application"
        orientation="horizontal"
        required
        disabled={readOnly}
      >
        {appChannels}
      </CheckboxGroup>
      {touched.channels && !!errors.channels && (
        <HelpText variant="error">{errors.channels}</HelpText>
      )}
    </FormControl>
  );
}
